import { invariant } from '../util/invariant';
import type { Vfs } from '../types/vfs';
import type { ItemArtefact } from '../dto/transferRequestDtos';
import { uniquePartialFilePath } from '../io/partialFilePath';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { StreamRoleId } from '../dto/mediaAvailability';
import { makePriority, PrioritySemaphore } from '../async/prioritySemaphore';
import { redactUrl } from '../format/redactUrl';
import { unorderedTaskExecution } from '../io/unorderedTaskExecution';
import type { PromiseSource, SettledPromise } from '../async/async';
import { MultiGuard, defer, getSettledValueOrThrow, neverCancel, settlePromise } from '../async/async';
import type { OpenResource, ResourceAccessor, ResourceLocation } from '../io/resource';
import { createUuid } from '../util/createUuid';
import { exceptionToString } from '../util/exception';
import { Timer } from '../util/timer';
import type { HighPrecisionMonotonicClock } from '../types/highPrecisionMonotonicClock';
import type { UrlProvider } from '../io/urlProvider';

const connectionBudget = 15;

/*
  Buffered url count

  Doesn't matter much for connection counts around 15 or higher, but does help
  slightly of connection counts around 6.
*/
export const bufferedUrlLimit = 20;

/*
  Concurrent connecting count.

  Experimentally derived heuristics.

  ```
  Concurrent
  connecting

   4 |             _-^
   3 |          _-^
   2 |       _-^
   1 | -----^
   0 |___________________
               1111111111  Concurrent
     01234567890123456789  transfers
  ```
*/

const concurrentConnectingLimit = Math.max(Math.round(connectionBudget / 3) - 1, 1);
const concurrentTransferLimit = Math.max(connectionBudget - concurrentConnectingLimit, 1);

/*
  Benchmark round 2:
                               urlRB  conRB  memRB  traCo
  STATS  86.78s  ±5.82s, bufs:     1      1      1      1
  STATS  24.05s  ±0.52s, bufs:     1      1      1     10
  STATS  84.95s ±12.41s, bufs:     1      1    1e7      1
  STATS 156.08s ±23.23s, bufs:     1     10      1      1
  STATS  76.13s ±12.05s, bufs:   100      1      1      1
  STATS  23.75s  ±0.89s, bufs:   100      1      1     20
  STATS  23.79s  ±0.60s, bufs:     1      1      1     20
  STATS  22.95s  ±0.35s, bufs:     1      1      1     40
  STATS  21.57s  ±1.00s, bufs:    10      5      1     10 (8)
  STATS  22.16s  ±0.65s, bufs:    20      5      1     10 (2)
  STATS  21.62s  ±0.61s, bufs:    27      5      1     10 (7)
  STATS  21.50s  ±0.83s, bufs:    35      5      1     10 (4)
  STATS  22.20s  ±0.90s, bufs:    39      5      1     10 (6)
  STATS  21.34s  ±1.13s, bufs:    42      5      1     10 (5)
  STATS  21.50s  ±1.20s, bufs:    50      5      1     10 (1)
  STATS  22.29s  ±0.87s, bufs:    80      5      1     10 (3)
  STATS  22.01s  ±0.75s, bufs:     1      2      1     10

  [Find the best setting for 15 connection budget]

  STATS  22.29s  ±3.10s, bufs:     1      2      1     13
  STATS  21.65s  ±0.82s, bufs:     1      3      1     12
  STATS  21.33s  ±0.64s, bufs:     1      4      1     11  <---
  STATS  21.57s  ±1.29s, bufs:     1      5      1     10
  STATS  22.78s  ±1.98s, bufs:     1      7      1      8

  STATS  21.54s  ±0.53s, bufs:     1      4      1     11
  STATS  21.57s  ±1.95s, bufs:    10      4      1     11
  STATS  21.56s  ±0.73s, bufs:    20      4      1     11
  STATS  21.99s  ±1.32s, bufs:    25      4      1     11
  STATS  21.25s  ±1.25s, bufs:    30      4      1     11
  STATS  21.50s  ±0.51s, bufs:    40      4      1     11
  STATS  21.33s  ±1.18s, bufs:    60      4      1     11

  [urlRB doesn't make much difference. Choosing:]
  STATS  21.54s  ±0.53s, bufs:     1      4      1     11

  [Find the best setting for 6 connection budget]

  STATS  31.29s  ±3.35s, bufs:     1      2      1      4
  STATS  35.78s  ±2.95s, bufs:     1      3      1      3
  STATS  27.09s  ±2.47s, bufs:     1      1      1      5 <---


  STATS  28.27s  ±0.61s, bufs:    30      1      1      5
  STATS  27.86s  ±2.62s, bufs:     1      1      1      5
  STATS  27.21s  ±1.50s, bufs:    30      1      1      5
  STATS  27.56s  ±2.48s, bufs:     1      1      1      5
  STATS  26.94s  ±1.51s, bufs:    30      1      1      5

  [Noisy data. Running with 25 samples.]
  STATS  28.48s  ±1.06s, bufs:     1      1      1      5
  STATS  27.83s  ±1.04s, bufs:    10      1      1      5
  STATS  27.30s  ±1.47s, bufs:    20      1      1      5 <--
  STATS  28.14s  ±1.37s, bufs:    30      1      1      5
*/

function writeChunkToTarget(tracker: FileTracker) {
  return async (task: SourceChunkBytes): Promise<TargetProgress | null> => {
    const trackedFile = tracker.getTrackerById(task.trackedTargetId);
    if (!trackedFile) {
      return null;
    }

    const what = 'offset:' + task.fileOffset;
    /*
      Byte writing pipeline
      ---------------------


      ```
      Reader: SourceChunkBytes.bytes
        ⇣
      Uint8Array
        ↓  Transform: countTransferredBytes
      Uint8Array
        ↳  Writable: writeBytesAtOffset
      ```

      Construction of this pipeline is implemented in writeChunkToTarget().
    */

    const countedBody = task.bytes.pipeThrough(countTransferredBytes(trackedFile.progress, trackedFile.structuredLogger, what, task.expectedLength));
    await countedBody.pipeTo(writeBytesAtOffset(trackedFile.target, task.fileOffset));
    return { trackedTargetId: task.trackedTargetId, totalNumberOfChunks: task.totalNumberOfChunks, numberOfCompletedChunks: ++trackedFile.writtenSegments };
  };
}

const streamIdByArtefact: Record<ItemArtefact, StreamRoleId.LiveRecordHighresTs | StreamRoleId.LiveRecordWebproxyTs | null> = {
  'highres': StreamRoleId.LiveRecordHighresTs,
  'proxy': StreamRoleId.LiveRecordWebproxyTs,
  'aaf': null
};

export function getTsStreamId(itemArtefact: ItemArtefact): StreamRoleId.LiveRecordHighresTs | StreamRoleId.LiveRecordWebproxyTs {
  const streamId = streamIdByArtefact[itemArtefact];
  invariant(streamId, 'Expected itemArtefact which implies a streamId, got' + itemArtefact);
  return streamId;
}

/*
  Overview of the transfer pipelines
  ==================================

  Summary
  ----------------

  The transfer pipeline is a utility transferring bytes using parallel transfer
  operations. For instance this could be downloading a file to disk from a HTTP
  server using multiple connections. It can also be transferring from disk to
  disk.

  The input to the transfer pipeline is a stream (ReadableStream) of information
  about the source chunks (SourceChunkInfo). These chunks may be sub divisions
  of a single data source, but chunks can also point to separate data sources
  so that the TransferPipeline can be used to stitch together files. Source
  chunks will typically refer to a byte range of a HTTP/file resource, but
  it may also be in-memory bytes.

  The output of the TransferPipeline is a Target. The transfer pipeline will
  write to the Target in the order that the SourceChunks are finished
  downloading. One example is the FileTarget which takes advantage of a sparse
  target file to write to the file out of order.


  Chunk transfer pipeline
  -------------------------

  ```
  SourceChunkInfo
    ↓  Transform: initiateReadOfSourceChunk
  InitiatedRead
    ↓  Transform: calculateTargetOffsets
  SourceChunkBytes
    ⇊  Transform: writeChunksToTarget (unordered concurrent processing)
  TargetProgress | null
    ↳  Writable: finalizeTargets
  ```

  Construction of this pipeline is implemented in
  TransferPipeline.prototype.getPipelineHead().


  Byte writing pipeline
  ---------------------

  ```
  Reader: SourceChunkBytes.bytes
    ⇣
  Uint8Array
    ↓  Transform: countTransferredBytes
  Uint8Array
    ↳  Writable: writeBytesAtOffset


  Construction of this pipeline is implemented in writeChunkToTarget().
  ```


  Pipeline chunk types
  ---------------------

  | Type            | Describes                                        |
  | --------------- | ------------------------------------------------ |
  | SourceChunkInfo | What to transfer, and what to use it for.        |
  | InitiatedRead   | A started web resource fetch operation.          |
  | SourceChunkBytes| A stream of bytes, and where to write it.        |
  | TargetProgress  | A structure describing how many of the source    |
  |                 | chunk have been written to the target.           |
  | Uint8Array      | Chunk of byte content                            |


  Pipeline transforms and writables
  ---------------------------------

  | Processing step           | Actions                                            |
  | ------------------------- | -------------------------------------------------- |
  | initiateReadOfSourceChunk | Initiate the read of the incoming chunk.           |
  | calculateTargetOffsets    | Use chunk size to advance target offset.           |
  | writeChunksToTarget       | Write byte stream to targets at given offsets.     |
  | finalizeTargets             | Move completed files, and notify completion.       |
  | countTransferredBytes     | Track and report transfer progress.                |
  | writeBytesAtOffset        | Write byte chunks to file at given offset.         |


  Parallelism
  -----------

  Once we reach the calculateTargetOffsets step we know the exact byte offset in
  the file to write a byte stream to. At this point we retrieve bytes from
  streams and write them to files concurrently.

  Up until the SourceChunkBytes step, we need to retain the order of the pipeline
  chunks since we need the offset of the previous SourceChunkBytes and the content
  length of the fetch response to calculate the offset of the next SourceChunkBytes
  for a given file.
*/

/**
 * What to transfer, and what to use it for.
 *
 * This is the basic transfer unit of the transfer pipeline. One or more of
 * these SourceChunkInfos will be piped into the transfer pipeline in order
 * to transfer a whole file.
 */

export interface SourceChunkInfo {
  resourceLocation: ResourceLocation,

  /**
   * The start and end of the byte range requested from the resource.
   * end is exclusive. If end is set to null it is until the end of
   * the resource.
   */
  start: number;
  end: number | null;

  /**
   * The total number of chunks that is needed to construct the whole target
   * file.
   *
   * Note completing writing the bytes of chunkIndex (totalNumberOfChunks - 1) to the
   * targetFile does not necessarily mean that the whole file has been
   * transferred, since the bytes from transferred chunks arrive in any order.
   *
   * Only when the bytes of totalNumberOfChunks number of chunks have been fully
   * written, then the file is complete.
   *
   * Note: This is required because the transfer pipeline will count the number
   * of written chunks and conclude the transfer operation once this number
   * has been reached. This is needed because the input to the transfer pipeline
   * is a stream of SourceChunkInfo which does not provide a "count" value. Since
   * the totalNumberOfChunks is only needed in order to conclude the transfer it
   * would be possible to implement this by counting the number of chunks as
   * part of the pipeline and once SourceChunkInfo stream reaches the end we
   * would know the total number of chunks. However, the total number of chunks
   * would still be needed in order to estimate progress anyway so this is
   * probably not worth the effort.
   */
  totalNumberOfChunks: number;
}

/**
 * This structure is the same as the SourceChunkInfo except it has been
 * assigned a target Id and a chunkIndex.
 */
export interface TrackedSourceChunkInfo {
  /**
   * The id of the tracker for the target
   */
  trackedTargetId: string;

  /**
   * Index of the chunk in the sequence of chunks that will construct the
   * whole transfer to a target.
   *
   * The task with chunkIndex 0 transfers the first bytes of the target file.
   * The task with chunkIndex (totalNumberOfChunks - 1) transfers the last bytes of
   * the target file.
   *
   */
  chunkIndex: number;

  chunkInfo: SourceChunkInfo,
}

interface Fetched {
  stream: ReadableStream<Uint8Array>,
  size: number,
}

/**
 * A started resource read operation. This could for instance be an initiated
 * HTTP fetch or a request to the file system to open a file. Note that
 * depending on the SourceChunk underlying data source this may be a no-op.
 */
interface InitiatedRead {
  /**
   * The id of the tracker for the target
   */
  trackedTargetId: string;

  /**
   * What to fetch, and what it should be used for.
   */
  chunkInfo: SourceChunkInfo;

  /**
   * Index of the chunk in the sequence of chunk that will construct the
   * target.
   *
   * The task with chunkIndex 0 transfers the first bytes of the target file.
   * The task with chunkIndex (totalNumberOfChunks - 1) transfers the last bytes of
   * the target file.
   *
   */
  chunkIndex: number;

  /**
   * The response that will eventually be provided by the started read
   * operation.
   */
  promise: SettledPromise<OpenResource>;
}

/**
 * A stream of bytes, and where to write it.
 */
interface SourceChunkBytes {
  /**
   * The stream of bytes that we're receiving.
   */
  bytes: ReadableStream<Uint8Array>;

  /**
   * Id of the tracker for the current target
   */
  trackedTargetId: string;

  /**
   * Where in the file to write the bytes.
   */
  fileOffset: number;

  /**
   * The exact number of bytes we expect to receive from the stream.
   */
  expectedLength: number;

  /**
   * The total number of chunks that is needed to construct the whole target
   * file.
   *
   * Passed on from StartedFetched
   */
  totalNumberOfChunks: number;
}

/**
 * How many chunks have been written to the target
 */
interface TargetProgress {
  /**
   * Id of the tracker for the current target
   */
  trackedTargetId: string;

  /**
   * The number of chunks that have been completely written to the target.
   */
  numberOfCompletedChunks: number;

  /**
   * The total number of chunks that needs to be completely written to the
   * target in order for the target to be considered complete.
   */
  totalNumberOfChunks: number;
}

/**
 * Start fetch() operations for each SourceChunkInfo.
 *
 * Transforms one SourceChunkInfo into one InitiatedRead.
 */
function initiateReadOfSourceChunk(resourceAccessor: ResourceAccessor, _signal: AbortSignal, tracker: FileTracker): TransformStream<TrackedSourceChunkInfo, InitiatedRead> {
  return new TransformStream<TrackedSourceChunkInfo, InitiatedRead>({
    transform(trackedSourceChunkInfo, controller) {
      const { trackedTargetId, chunkInfo, chunkIndex } = trackedSourceChunkInfo;
      const trackedFile = tracker.getTrackerById(trackedTargetId);
      if (!trackedFile) {
        return;
      }
      trackedFile.structuredLogger.log({ message: 'Initiating read of chunk', index: chunkIndex, total: chunkInfo.totalNumberOfChunks, level: 'debug', resource: chunkInfo.resourceLocation });
      const promise = resourceAccessor.open(chunkInfo.resourceLocation, chunkInfo.start, chunkInfo.end)
        .then((openResource) => {
          return openResource;
        });
      controller.enqueue({ trackedTargetId, chunkInfo, chunkIndex, promise: settlePromise(promise) });
    }
  }, undefined, new CountQueuingStrategy({ highWaterMark: concurrentConnectingLimit }));
}

function rejectTrackedFile(err: any, chunkInfo: SourceChunkInfo, trackedFile: TrackedFile) {
  err.info = {
    trackedTargetId: trackedFile.id,
    url: chunkInfo.resourceLocation.type === 'http' ? redactUrl(chunkInfo.resourceLocation.provider.getUrl()) : undefined,
    filename: chunkInfo.resourceLocation.type === 'file' ?  chunkInfo.resourceLocation.filename : undefined,
  };
  trackedFile.completion.deferred.reject(err);
}

/**
 * Use Content-Length to advance file offset.
 *
 * Transforms one InitiatedRead into one SourceChunkBytes.
 */
function calculateTargetOffsets(tracker: FileTracker) {
  return new TransformStream<InitiatedRead, SourceChunkBytes>({
    async transform(responseTask: InitiatedRead, controller) {
      const trackedFile = tracker.getTrackerById(responseTask.trackedTargetId);
      if (!trackedFile) {
        return;
      }

      const debugInfo = {
        index: responseTask.chunkIndex,
        total: responseTask.chunkInfo.totalNumberOfChunks,
        resource: responseTask.chunkInfo.resourceLocation
      };

      let fetched: Fetched;
      try {
        trackedFile.structuredLogger.log({ level: 'debug', message: 'Waiting for chunk read to initiate', ...debugInfo });
        fetched = await getSettledValueOrThrow(responseTask.promise);
      } catch (err) {
        rejectTrackedFile(err, responseTask.chunkInfo, trackedFile);
        return;
      }
      trackedFile.structuredLogger.log({ level: 'debug', message: 'Chunk read operation initiated', ...debugInfo });

      trackedFile.progress.startSegment(fetched.size, responseTask.chunkInfo.totalNumberOfChunks);
      if (trackedFile.nextIndex != responseTask.chunkIndex) {
        trackedFile.structuredLogger.log({ level: 'warn', message: 'Ignoring out of order chunk', ...debugInfo });
        /*
          Earlier chunks have been rejected, which means this chunk is
          useless.

          TODO: Should this not throw an error? A warning seems quite light way to handle a complete failure.
        */
        return;
      }
      const fileOffset = trackedFile.nextOffset;

      trackedFile.nextOffset += fetched.size; ++trackedFile.nextIndex;

      controller.enqueue({
        trackedTargetId: responseTask.trackedTargetId,
        fileOffset,
        bytes: fetched.stream,
        expectedLength: fetched.size,
        totalNumberOfChunks: responseTask.chunkInfo.totalNumberOfChunks,
      });
    }
  });
}

/**
 * Converts SourceChunkInfos to TrackedSourceChunkInfos
 */
function bindToTracker(trackedTargetId: string): TransformStream<SourceChunkInfo, TrackedSourceChunkInfo> {
  let chunkIndex = 0;
  return new TransformStream<SourceChunkInfo, TrackedSourceChunkInfo>({
    transform(chunkInfo: SourceChunkInfo, controller) {
      controller.enqueue({ trackedTargetId, chunkInfo, chunkIndex });
      chunkIndex += 1;
    }
  });
}

/**
 * Write byte streams to files at given offsets.
 *
 * Transforms one SourceChunkBytes to one TargetProgress, however the order of
 * output TargetProgress will not correspond to the order of input
 * SourceChunkBytes.
 *
 * Will process multiple SourceChunkBytes in parallel.
 */
function writeChunksToTarget(tracker: FileTracker): TransformStream<SourceChunkBytes, TargetProgress | null> {
  return unorderedTaskExecution(writeChunkToTarget(tracker), new CountQueuingStrategy({ highWaterMark: concurrentTransferLimit }));
}



/**
 * Move completed files, and notify completion.
 *
 * Ignores TargetProgress chunks that do not represent the completion of writing
 * the whole file.
 */
function finalizeTargets(tracker: FileTracker) {
  return new WritableStream<TargetProgress | null>({
    async write(writeResult) {
      if (writeResult && writeResult.numberOfCompletedChunks == writeResult.totalNumberOfChunks) {
        const trackedFile = tracker.getTrackerById(writeResult.trackedTargetId);
        if (!trackedFile) {
          return;
        }

        await trackedFile.target.close();
        trackedFile.completion.deferred.resolve();
      }
    }
  });
}


function createFlatFileUrlStream(urlProvider: UrlProvider, chunkSize: number, totalNumberOfChunks: number): ReadableStream<SourceChunkInfo> {
  const ab = new AbortController();
  let nextSegmentIndex = 0;
  return new ReadableStream<SourceChunkInfo>({
    async pull(controller) {
      const chunkIndex = nextSegmentIndex++;
      const start = (chunkSize * chunkIndex);
      let end : number | null = null;
      if ((nextSegmentIndex < totalNumberOfChunks) && (totalNumberOfChunks > 1)) {
        end = chunkSize * nextSegmentIndex;
      }

      controller.enqueue({
        resourceLocation: { type: 'http', provider: urlProvider },
        totalNumberOfChunks,
        start,
        end,
      });

      if (nextSegmentIndex >= totalNumberOfChunks) {
        controller.close();
      }
    },
    cancel() {
      ab.abort();
    }
  }, new CountQueuingStrategy({ highWaterMark: bufferedUrlLimit }));
}

class SegmentProgress {
  knownLength = 0;
  processedSegmentSize: number = 0;
  processedSegmentCount : number = 0;
  position = 0;
  pendingSegmentCount: number | null = null;
  constructor(private progressId: string | undefined) { }

  startSegment(actualLength: number, totalNumberOfChunks: number) {
    this.processedSegmentSize += actualLength;
    this.processedSegmentCount += 1;
    this.knownLength += actualLength;
    if (this.pendingSegmentCount === null) {
      this.pendingSegmentCount = totalNumberOfChunks;
    }
    --this.pendingSegmentCount;
  }

  advance(count: number) {
    this.position += count;
  }

  log(structuredLogger: StructuredLogger) {
    if ((this.pendingSegmentCount === null) || (this.processedSegmentCount === 0)) {
      structuredLogger.log({ message: 'Preparing transfer' });
    } else {
      const averageSegmentSize = (this.processedSegmentSize / this.processedSegmentCount);
      const length = this.knownLength + averageSegmentSize * this.pendingSegmentCount;
      if (this.progressId !== undefined) {
        structuredLogger.log({ message: 'progress', progress: { [this.progressId]: { position: this.position, length, unit: 'bytes' } } });
      }
    }
  }
}

interface TrackedFile {
  id: string,
  target: Target,
  structuredLogger: StructuredLogger;
  progress: SegmentProgress;
  nextOffset: number;
  nextIndex: number;
  writtenSegments: number;
  completion: PromiseSource<void>;
}

export interface Target {
  write(bytes: Uint8Array, offset: number): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
  id(): string;
}

export async function createFileTarget(vfs: Vfs, targetPath: string): Promise<Target> {
  // Use a unique temporary file path that is not likely to conflict with other
  // simultaneous access to the same file, e.g. another client downloading the same
  // file to the same shared path.
  const writePath = uniquePartialFilePath(targetPath);
  const file = await vfs.open(writePath, 'w');
  return {
    async write(bytes: Uint8Array, offset: number): Promise<void> {
      while (bytes.length > 0) {
        const written = await file.write(bytes, 0, bytes.length, offset);
        bytes = bytes.slice(written);
        offset += written;
      }
    },
    async close(): Promise<void> {
      await file.close();
      await vfs.rename(writePath, targetPath);
    },
    async abort(): Promise<void> {
      await file.close();
      await vfs.unlink(writePath);
    },
    id(): string {
      return `file:${targetPath}`;
    }
  };
}

/**
 *  TransferPipelien output target adapter for a WritableStream of bytes.
 *
 *  @param clock This parameter is only needed for performance stats. If there
 *  is no clock available performance stats can be disabled by pass in null.
 */
export async function createStreamTarget(
  logger: StructuredLogger,
  writableStream: WritableStream<Uint8Array>,
  clock: HighPrecisionMonotonicClock | null,
): Promise<Target> {
  const timer = (clock !== null) ? new Timer(clock) : null;
  timer?.start('total');
  let totalPendingBytes = 0;
  let totalPendingChunks = 0;
  const memoryReportInterval = 1000;
  let nextMemoryReport = Date.now() + memoryReportInterval;
  const onPendingBytesChange = (change: number) => {
    totalPendingBytes += change;
    totalPendingChunks += (change > 0) ? 1 : -1;
    const now = Date.now();
    if (now > nextMemoryReport) {
      nextMemoryReport = now + memoryReportInterval;
      logger.log({ level: 'info', message: `Stream output memory usage: ${Math.floor(totalPendingBytes / (1024 * 1024))} MB in ${totalPendingChunks} chunks` });
    }
  };

  const pendingChunks = new Map<number, Uint8Array>();

  const maxMemoryUsage = 256 * 1024 * 1024;
  const chunkWritten = new MultiGuard();
  const chunkAvailable = new MultiGuard();
  let done = false;
  let aborted = false;
  let writerError : any;
  let currentStreamOffset = 0;
  const writerThread = async () => {
    const writer = writableStream.getWriter();
    while (!aborted) {
      if ((pendingChunks.size === 0) && done) {
        break;
      }
      const chunk = pendingChunks.get(currentStreamOffset);
      if ((chunk === undefined)) {
        timer?.start('write:waiting');
        await chunkAvailable.wait();
        timer?.end('write:waiting');
        continue;
      }
      pendingChunks.delete(currentStreamOffset);
      timer?.start('write:out');
      await writer.write(chunk);
      timer?.end('write:out');
      currentStreamOffset += chunk.length;
      chunkWritten.notifyAll();
      onPendingBytesChange(-chunk.length);
    }
    if (aborted) {
      await writer.abort();
    } else {
      await writer.close();
    }
  };

  const writerPromise =
    writerThread()
      .then(() => {
        logger.log({ level: 'info', message: 'Writer thread finished' });
      })
      .catch((error) => {
        logger.log({ level: 'error', message: `Writer thread failed: ${exceptionToString(error)}` });
        writerError = error;
        chunkWritten.notifyAll();
      });

  const id = createUuid();
  return {
    async write(bytes: Uint8Array, offset: number): Promise<void> {
      while (offset > currentStreamOffset + maxMemoryUsage) {
        if (writerError) {
          throw writerError;
        }
        await chunkWritten.wait();
      }

      onPendingBytesChange(bytes.length);
      pendingChunks.set(offset, bytes);
      chunkAvailable.notifyAll();
    },
    async close(): Promise<void> {
      done = true;
      chunkAvailable.notifyAll();

      if (timer !== null) {
        timer.end('total');
        logger.log({ level: 'info', message: `Stream transfer target performance report: ${timer.report()}` });
      }

      await writerPromise;

      if (writerError) {
        throw writerError;
      }
    },
    async abort(): Promise<void> {
      aborted = true;
      chunkAvailable.notifyAll();

      await writerPromise;
    },
    id(): string {
      return `stream:${id}`;
    }
  };
}

class FileTracker {
  constructor(signal: AbortSignal) {
    signal.addEventListener('abort', ev => this.abortAll(ev));
  }

  trackedByTargetPath: Record<string, TrackedFile> = {};
  async startTrackingFile(target: Target, structuredLogger: StructuredLogger, progressId: string | undefined): Promise<TrackedFile> {
    const trackedTargetId = target.id();
    invariant(this.trackedByTargetPath[trackedTargetId] == null, 'Did not expect file to already be tracked');
    const progress = new SegmentProgress(progressId);
    const tf: TrackedFile = { id: trackedTargetId, target, progress, nextOffset: 0, nextIndex: 0, writtenSegments: 0, completion: defer<void>(), structuredLogger };
    this.trackedByTargetPath[trackedTargetId] = tf;
    tf.completion.promise.catch(async () => {
      await target.abort();
    }).catch(() => {
      // Pass. We care about the main error, not any follow-up VFS errors.
    }).finally(() => {
      delete this.trackedByTargetPath[trackedTargetId];
    });
    return tf;
  }

  getTrackerById(id: string): TrackedFile | null {
    return this.trackedByTargetPath[id];
  }

  abortAll(cause: any) {
    for (const tf of Object.values(this.trackedByTargetPath)) {
      tf.completion.deferred.reject(cause);
    }
  }
}


function countTransferredBytes(progress: SegmentProgress, structuredLogger: StructuredLogger, what: string, expectedLength: number) {
  let counted = 0;
  structuredLogger.log({ message: 'Starting stream', what, level: 'debug' });
  return new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, controller) {
      progress.advance(chunk.byteLength);
      progress.log(structuredLogger);

      counted += chunk.byteLength;
      if (counted > expectedLength) {
        throw new Error('Expected ' + expectedLength + ' but has received ' + counted + ' so far');
      }

      controller.enqueue(chunk);

    },
    flush() {
      structuredLogger.log({ message: 'Stopped stream', what, level: 'debug' });
    }
  });
}


function writeBytesAtOffset(target: Target, fileOffset: number) {
  return new WritableStream<Uint8Array>({
    async write(chunk) {
      await target.write(chunk, fileOffset);
      fileOffset += chunk.length;
    }
  });
}

export class TransferPipeline {
  tracker: FileTracker;
  pipelineHead: WritableStream<TrackedSourceChunkInfo> | null = null;
  semaphore = new PrioritySemaphore(1);

  constructor(
    private signal: AbortSignal,
    private resourceAccessor: ResourceAccessor,
    private vfs: Vfs,
  ) {
    this.tracker = new FileTracker(signal);
  }

  getPipelineHead() {
    if (!this.pipelineHead) {
      /*
        SourceChunkInfo
         ↓ Transform: initiateReadOfSourceChunk
        InitiatedRead
         ↓ Transform: calculateTargetOffsets
        SourceChunkBytes
         ⇊ Transform: writeChunksToTarget (unordered concurrent execution)
        TargetProgress | null
         ↳ finalizeTargets
      */
      const transferQueueHead = initiateReadOfSourceChunk(this.resourceAccessor, this.signal, this.tracker);
      const writeTasks = transferQueueHead.readable.pipeThrough(calculateTargetOffsets(this.tracker));
      const writeResults = writeTasks.pipeThrough(writeChunksToTarget(this.tracker));
      writeResults.pipeTo(finalizeTargets(this.tracker)).catch(ex => {
        this.tracker.abortAll(ex);
      });
      this.pipelineHead = transferQueueHead.writable;
    }
    return this.pipelineHead;
  }

  async transfer(
    parameters: {
      chunkInfos: ReadableStream<SourceChunkInfo>,
      target: Target,
      logger: StructuredLogger,
      progressId?: string,
    }
  ) {
    const { chunkInfos, target, logger, progressId } = parameters;
    const trackedFile = await this.tracker.startTrackingFile(target, logger, progressId);
    const head : WritableStream<TrackedSourceChunkInfo> = this.getPipelineHead();
    void this.semaphore.limit(async () => {
      try {
        await chunkInfos
          .pipeThrough(bindToTracker(trackedFile.id))
          .pipeTo(head, { preventClose: true, preventAbort: true });
      } catch (ex) {
        trackedFile.completion.deferred.reject(ex);
      }
    }, makePriority(), neverCancel);
    await trackedFile.completion.promise;
  }

  async transferFlatFileReference(urlProvider: UrlProvider, mediaSize: number | null, logger: StructuredLogger, outPath: string) {
    const progressId = outPath;
    logger.log({ message: 'progress', progress: { [progressId]: { position: 0, length: mediaSize ?? NaN, unit: 'bytes' } } });

    let chunkSize;
    let chunkCount;

    if (mediaSize != null) {
      /*
        Nominal chunk size selected based on recommendations from
        https://docs.aws.amazon.com/whitepapers/latest/s3-optimizing-performance-best-practices/use-byte-range-fetches.html
      */
      chunkSize = 8 * 1024 * 1024;
      chunkCount = Math.ceil(mediaSize / chunkSize);
    } else {
      chunkSize = Number.MAX_SAFE_INTEGER;
      chunkCount = 1;
    }

    const chunkInfos = createFlatFileUrlStream(urlProvider, chunkSize, chunkCount);
    const target = await createFileTarget(this.vfs, outPath);
    await this.transfer({ chunkInfos, target, logger, progressId });
  }
}

export function getTransferParameters() {
  return [bufferedUrlLimit, concurrentConnectingLimit, concurrentTransferLimit];
}
